/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { useOktaAuth } from "@okta/okta-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactGA from "react-ga4";
import { hotjar } from "react-hotjar";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppContext } from "@/app/context/app-context";
import { useAllFeatureToggleValues } from "@/app/hooks/use-feature-toggle/use-all-feature-toggle-values";
import { useHasPermissions } from "@/app/hooks/use-has-permission/use-has-permission";
import { useSidebarOpen } from "@/app/hooks/use-sidebar-open";
import { UserRoles } from "@/app/lib/role-based-access-control";
import { flattenPrivateRoutes } from "@/app/navigation/routes/private-routes";
import { PrivateRoutes } from "@/app/navigation/routes/routes.type";
import {
  BurstSVG,
  ContractSVG,
  CounterOfferSVG,
  FlashSVG,
  FolderTimeSVG,
  HorizontalStackSVG,
  HouseListSVG,
  IncomingOfferSVG,
  LogSVG,
  OutgoingOfferSVG,
  TrelloSVG,
  UserRotateSVG,
} from "@/assets/icons";

import {
  ENABLE_GA_TRACKING,
  ENABLE_HOTJAR_TRACKING,
  GA_MEASUREMENT_ID,
  HOTJAR_ID,
  HOTJAR_SNIPPET_VERSION,
} from "../../lib/env-helpers";
import { AccountStatus } from "../../types/generated/graphql";
import { UseLayout } from "./layout.types";

export const getIconComponents = (): Record<string, any> => ({
  AdminDashboard: <TrelloSVG />,
  RecHoldings: <BurstSVG />,
  HorizontalStack: <HorizontalStackSVG />,
  UserResources: <UserRotateSVG />,
  OrderHistory: <FolderTimeSVG />,
  Markets: <HouseListSVG />,
  SpotTrade: <FlashSVG />,
  ForwardTrade: <ContractSVG />,
  Log: <LogSVG />,
  CounterOffer: <CounterOfferSVG />,
  IncomingOffer: <IncomingOfferSVG />,
  OutgoingOffer: <OutgoingOfferSVG />,
});

export const useLayout: UseLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userAccountInfo } = useAppContext();

  const {
    toggles: { enableCrossRegistryForwardTrading },
  } = useAllFeatureToggleValues();

  const { sidebarOpen, toggleSidebarOpen } = useSidebarOpen();
  const { hasRolePermissions, hasTypePermissions, isRequiredFeatureTogglesEnabled, hasRequiredRegistries } =
    useHasPermissions();
  const [expandedRoutes, setExpandedRoutes] = useState<string[]>([]);
  const { authState } = useOktaAuth() ?? {};

  const handleToggleRoute = useCallback(
    (link: string) =>
      expandedRoutes?.includes(link)
        ? setExpandedRoutes((state) => state.filter((route) => route !== link))
        : setExpandedRoutes((state) => [...state, link]),
    [expandedRoutes],
  );

  const activeRoutes = useMemo(() => {
    const routes = location.pathname.split("/");
    for (let i = 1; i < routes.length; i++) {
      routes[i] = `${routes[i - 1]}/${routes[i]}`;
    }
    return flattenPrivateRoutes.filter((route) => routes.includes(route.path));
  }, [location.pathname]);

  const getFilteredRoutes = (routes: PrivateRoutes[]) =>
    routes.filter(
      ({ layout, guard }) =>
        // ignore status check; show all routes regardless of user status
        !!layout &&
        hasRolePermissions(guard.allowedRoles) &&
        hasTypePermissions(guard.allowedAccountTypes) &&
        isRequiredFeatureTogglesEnabled(guard.requiredFeatureToggles) &&
        hasRequiredRegistries(guard.allowedRegistries),
    );

  useEffect(() => {
    if (ENABLE_HOTJAR_TRACKING && userAccountInfo?.status === AccountStatus.Active) {
      hotjar.initialize(Number(HOTJAR_ID), Number(HOTJAR_SNIPPET_VERSION));
    }
    if (ENABLE_GA_TRACKING) {
      ReactGA.initialize(GA_MEASUREMENT_ID, {
        gaOptions: {
          debug_mode: true,
          userId: authState?.accessToken?.claims?.uid,
        },
        gtagOptions: {
          debug_mode: true,
        },
      });
    }
  }, [userAccountInfo?.status, authState]);

  const canViewRegistry = useMemo(() => hasRolePermissions([UserRoles.RoleAdmin]), [hasRolePermissions]);

  const handleNavigate = useCallback(
    (path: string, subPath: string) => {
      navigate(path + subPath);
    },
    [navigate],
  );

  const hideRegistryDropdown = useMemo(
    () => enableCrossRegistryForwardTrading && activeRoutes.at(-1)?.guard.allowCrossRegistryTrading,
    [enableCrossRegistryForwardTrading, activeRoutes],
  );

  return {
    sidebarOpen,
    updateSidebarState: toggleSidebarOpen,
    accountStatus: userAccountInfo?.status,
    handleToggleRoute,
    activeRoutes,
    expandedRoutes,
    userInfo: {
      name: `${userAccountInfo?.firstName ?? ""} ${userAccountInfo?.lastName ?? ""}`,
      email: userAccountInfo?.email ?? "",
      accountType: userAccountInfo?.accountType,
      canViewRegistry,
    },
    handleNavigate,
    getFilteredRoutes,
    hideRegistryDropdown,
  };
};
